import React from 'react';
import {
  AlertTriangle, BookOpen, CalendarClock, CheckCircle2, Clock, Code2, ExternalLink, Flag, GitCommitHorizontal,
  KeyRound, ListChecks, Mic, Sparkles, Users,
} from 'lucide-react';
import { HoursProjection } from '../ProjectPage/roadmap/Steering';
import { hours } from '../ProjectPage/ui';
import { ago, longDate, parsePercent, shortDate } from './review';

// Un projet de la revue d'avancement : à gauche ce qui s'est passé depuis le dernier regard et ce qui reste à faire,
// à droite le % de chaque itération en cours avec ses repères (temps écoulé, heures, objectifs) et la projection.

const KIND = {
  tactical: { icon: Users, tone: 'text-indigo-500', label: 'Tactique' },
  meeting: { icon: Mic, tone: 'text-sky-500', label: 'Réunion' },
  journal: { icon: BookOpen, tone: 'text-slate-400', label: 'Journal' },
  code: { icon: Code2, tone: 'text-slate-400', label: 'Code' },
  objective_done: { icon: CheckCircle2, tone: 'text-emerald-500', label: 'Objectif terminé' },
  milestone_done: { icon: Flag, tone: 'text-emerald-600', label: 'Jalon atteint' },
  roadmap: { icon: GitCommitHorizontal, tone: 'text-violet-500', label: 'Roadmap' },
};

const STATUS = {
  blocked: { label: 'bloqué', tone: 'bg-rose-50 text-rose-700' },
  in_progress: { label: 'en cours', tone: 'bg-amber-50 text-amber-700' },
  not_started: { label: 'à faire', tone: 'bg-slate-100 text-slate-600' },
};

const Section = ({ title, aside, children }) => (
  <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {aside && <span className="text-xs text-slate-400">{aside}</span>}
    </div>
    {children}
  </section>
);

const Stat = ({ value, label, title }) => (
  <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2" title={title}>
    <p className="text-lg font-bold tabular-nums text-slate-900">{value}</p>
    <p className="truncate text-[11px] text-slate-500">{label}</p>
  </div>
);

const EventLine = ({ event }) => {
  const kind = KIND[event.kind] || KIND.journal;
  const Icon = kind.icon;
  return (
    <li className="flex gap-3 py-2">
      <span className="w-14 shrink-0 pt-0.5 text-[11px] font-medium tabular-nums text-slate-400">{shortDate(event.date)}</span>
      <Icon size={14} className={`mt-0.5 shrink-0 ${kind.tone}`} aria-label={kind.label} />
      <div className="min-w-0 flex-1 text-sm leading-snug">
        {event.items ? (
          <>
            <p className="text-xs font-medium text-slate-500">Dit en tactique</p>
            <ul className="mt-0.5 space-y-0.5 text-slate-700">
              {event.items.map((item) => <li key={item}>· {item}</li>)}
            </ul>
          </>
        ) : (
          <>
            <p className="text-slate-800">{event.text}</p>
            {event.detail && <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{event.detail}{event.by ? ` (${event.by})` : ''}</p>}
          </>
        )}
      </div>
    </li>
  );
};

/** Ce qui s'est passé depuis le dernier regard (ou ces deux dernières semaines). */
const Since = ({ data }) => {
  const { carnet, events, hours_since: spent } = data;
  const title = data.since_known ? `Depuis le ${longDate(data.since)}` : 'Ces deux dernières semaines';
  const quiet = !carnet.news.length && !events.length && !spent.total;
  return (
    <Section title={title} aside={data.since_known ? 'dernier point d’avancement' : 'jamais noté'}>
      <div className="grid grid-cols-3 gap-2">
        <Stat value={hours(spent.total)} label="passées sur le projet"
          title={spent.people.map((p) => `${p.name} : ${hours(p.hours)}`).join('\n') || 'Aucune heure saisie'} />
        <Stat value={data.objectives_done} label={data.objectives_done > 1 ? 'objectifs terminés' : 'objectif terminé'} />
        <Stat value={data.tasks_done} label={data.tasks_done > 1 ? 'tâches terminées' : 'tâche terminée'} />
      </div>
      {spent.people.length > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          {spent.people.slice(0, 5).map((p) => `${p.name.split(' ')[0]} ${hours(p.hours)}`).join(' · ')}
          {spent.people.length > 5 && ` · +${spent.people.length - 5}`}
        </p>
      )}
      {carnet.news.length > 0 && (
        <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-500">
            <Sparkles size={12} /> Quoi de neuf, d’après Nexus
          </p>
          <ul className="space-y-1 text-sm text-slate-700">
            {carnet.news.map((n) => (
              <li key={`${n.date}${n.text}`} className="flex gap-2">
                <span className="w-12 shrink-0 text-[11px] tabular-nums text-slate-400">{shortDate(n.date)}</span>
                <span className="min-w-0">{n.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {events.length > 0 && <ul className="mt-3 divide-y divide-slate-100">{events.map((e, i) => <EventLine key={`${e.kind}${e.ref || i}`} event={e} />)}</ul>}
      {quiet && <p className="mt-3 text-sm text-slate-400">Rien n’a été tracé dans Deepflow sur ce projet depuis (ni heures, ni journal, ni réunion).</p>}
    </Section>
  );
};

/** Ce qui reste : objectifs ouverts des itérations en cours, jalons, points de vigilance tenus par Nexus. */
const Remaining = ({ data }) => {
  const { remaining, carnet } = data;
  const [first] = data.iterations;
  return (
    <Section title="Ce qui reste"
      aside={remaining.total ? `${remaining.done} / ${remaining.total} objectifs ${data.iterations.length > 1 ? 'des itérations en cours' : 'de l’itération'} terminés` : null}>
      {first && first.state !== 'done' && (
        <p className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
          <span className="flex items-center gap-1"><CalendarClock size={12} className="text-slate-400" />
            {first.days_left} jour{first.days_left > 1 ? 's' : ''} ouvré{first.days_left > 1 ? 's' : ''} jusqu’au {shortDate(first.end_date)}</span>
          {first.hours.budget > 0 && (
            <span className="flex items-center gap-1"><Clock size={12} className="text-slate-400" />
              {first.hours.budget - first.hours.consumed >= 0
                ? `${hours(first.hours.budget - first.hours.consumed)} de budget restant`
                : `budget dépassé de ${hours(first.hours.consumed - first.hours.budget)}`}</span>
          )}
        </p>
      )}
      {remaining.objectives.length > 0 ? (
        <ul className="space-y-1">
          {remaining.objectives.slice(0, 10).map((o) => (
            <li key={o.id} className="flex items-start gap-2 text-sm">
              <ListChecks size={14} className="mt-0.5 shrink-0 text-slate-300" />
              <span className="min-w-0 flex-1 leading-snug text-slate-700">{o.name}
                {o.owner && <span className="text-xs text-slate-400"> · {o.owner.split(' ')[0]}</span>}
              </span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${(STATUS[o.status] || STATUS.not_started).tone}`}>
                {(STATUS[o.status] || STATUS.not_started).label}
              </span>
            </li>
          ))}
          {remaining.objectives.length > 10 && <li className="pl-6 text-xs text-slate-400">et {remaining.objectives.length - 10} autres</li>}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">
          {remaining.total ? 'Tous les objectifs de l’itération sont terminés.' : 'Pas d’objectif rangé dans les sprints de l’itération.'}
        </p>
      )}
      {remaining.milestones.length > 0 && (
        <div className="mt-4 space-y-1 border-t border-slate-100 pt-3">
          {remaining.milestones.map((m) => (
            <p key={m.id} className="flex items-center gap-2 text-sm">
              <Flag size={13} className={m.late ? 'text-rose-500' : 'text-slate-400'} />
              <span className="min-w-0 flex-1 truncate text-slate-700">{m.name}</span>
              <span className={`text-xs tabular-nums ${m.late ? 'font-medium text-rose-600' : 'text-slate-500'}`}>
                {m.late ? `en retard (${shortDate(m.date)})` : shortDate(m.date)}
              </span>
            </p>
          ))}
        </div>
      )}
      {carnet.risks.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-600">
            <AlertTriangle size={12} /> Points de vigilance
          </p>
          <ul className="space-y-0.5 text-sm text-slate-600">{carnet.risks.map((r) => <li key={r}>· {r}</li>)}</ul>
        </div>
      )}
    </Section>
  );
};

const Gauge = ({ label, value, tone, hint }) => (
  <div>
    <div className="mb-0.5 flex items-baseline justify-between text-[11px]">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold tabular-nums text-slate-700">{value == null ? '—' : `${Math.round(value)} %`}</span>
    </div>
    <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, Math.max(0, value || 0))}%` }} />
    </div>
    {hint && <p className="mt-0.5 text-[10px] text-slate-400">{hint}</p>}
  </div>
);

const STEPS = [-10, -5, 5, 10];

/** % d'une itération : dernière note, repères, saisie (curseur + nombre), projection des heures. */
export const IterationInput = ({ iteration, draft, onChange, objectives, readOnly, autoFocus }) => {
  const last = iteration.progress?.percent ?? null;
  const text = draft.value ?? (last == null ? '' : String(Math.round(last * 10) / 10));
  const value = parsePercent(text);
  const delta = value != null && last != null ? value - last : null;
  const set = (next) => onChange({ ...draft, value: String(Math.min(100, Math.max(0, Math.round(next)))) });
  const consumedPct = iteration.hours.budget > 0 ? (iteration.hours.consumed / iteration.hours.budget) * 100 : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-semibold text-slate-900">{iteration.name}</span>
        <span className="text-xs text-slate-400">{shortDate(iteration.start_date)} → {shortDate(iteration.end_date)}</span>
        {iteration.state !== 'current' && (
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
            {iteration.state === 'done' ? 'terminée' : 'à venir'}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {iteration.progress
          ? <>Dernière note : <span className="font-semibold text-slate-700">{Math.round(last)} %</span>, {ago(iteration.progress.created_at)} par {iteration.progress.employee_name || 'quelqu’un'}{iteration.progress.comment ? <> : « {iteration.progress.comment} »</> : ''}</>
          : 'Jamais estimée.'}
      </p>

      <div className="mt-3 flex items-center gap-3">
        <input type="range" min="0" max="100" step="5" value={value ?? last ?? 0} disabled={readOnly}
          onChange={(e) => set(Number(e.target.value))} aria-label={`% de réalisation de ${iteration.name}`}
          className="h-2 min-w-0 flex-1 cursor-pointer accent-indigo-600 disabled:cursor-default" />
        <div className="flex items-baseline gap-1">
          <input value={text} inputMode="decimal" disabled={readOnly} autoFocus={autoFocus}
            onChange={(e) => onChange({ ...draft, value: e.target.value })} onFocus={(e) => e.target.select()}
            aria-label="% de réalisation"
            className={`w-16 rounded-lg border px-2 py-1 text-right text-xl font-bold tabular-nums outline-none focus:ring-2 focus:ring-indigo-100 ${value == null && text !== '' ? 'border-rose-300 text-rose-600' : 'border-slate-200 text-slate-900'}`} />
          <span className="text-sm font-semibold text-slate-500">%</span>
        </div>
      </div>
      {!readOnly && (
        <div className="mt-1.5 flex items-center gap-1">
          {STEPS.map((step) => (
            <button key={step} type="button" onClick={() => set((value ?? last ?? 0) + step)}
              className="rounded-md border border-slate-200 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-slate-500 hover:border-indigo-300 hover:text-indigo-700">
              {step > 0 ? `+${step}` : `−${-step}`}
            </button>
          ))}
          {delta != null && Math.abs(delta) >= 0.05 && (
            <span className={`ml-auto text-xs font-semibold tabular-nums ${delta > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {delta > 0 ? '+' : '−'}{Math.abs(Math.round(delta))} pts
            </span>
          )}
          {delta != null && Math.abs(delta) < 0.05 && <span className="ml-auto text-xs text-slate-400">inchangé</span>}
        </div>
      )}

      <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
        <Gauge label="Temps de l’itération écoulé" value={iteration.elapsed_pct} tone="bg-slate-400"
          hint={iteration.state === 'current' ? `${iteration.days_left} jour${iteration.days_left > 1 ? 's' : ''} ouvré${iteration.days_left > 1 ? 's' : ''} restant${iteration.days_left > 1 ? 's' : ''}` : null} />
        <Gauge label="Budget temps consommé" value={consumedPct} tone={consumedPct > 100 ? 'bg-rose-500' : 'bg-amber-500'}
          hint={iteration.hours.budget > 0 ? `${hours(iteration.hours.consumed)} / ${hours(iteration.hours.budget)}` : 'pas de budget temps'} />
        {objectives.total > 0 && (
          <Gauge label="Objectifs terminés" value={(objectives.done / objectives.total) * 100} tone="bg-emerald-500"
            hint={`${objectives.done} sur ${objectives.total}`} />
        )}
        <HoursProjection consumed={iteration.hours.consumed} budget={iteration.hours.budget} percent={value ?? last} />
      </div>
      {!readOnly && (
        <input value={draft.comment || ''} onChange={(e) => onChange({ ...draft, comment: e.target.value })} maxLength={400}
          placeholder="Ce qui le justifie (facultatif)"
          className="mt-3 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none placeholder:text-slate-400 focus:border-indigo-200 focus:ring-2 focus:ring-indigo-50" />
      )}
    </div>
  );
};

/** En-tête du projet et les deux colonnes (le panneau de saisie est passé par la page). */
const ProjectReview = ({ data, panel, onOpenProject }) => {
  const holders = data.roadmap?.holders || [];
  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="min-w-0 space-y-3 lg:col-start-1 lg:row-start-1">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-slate-900">{data.project.name}</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {[data.project.client_name, data.project.project_number && `n° ${data.project.project_number}`].filter(Boolean).join(' · ')}
              {data.roadmap?.can === false && holders.length > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-violet-700"><KeyRound size={12} /> roadmap tenue par {holders.map((h) => h.name).join(', ')}</span>
              )}
            </p>
          </div>
          <button type="button" onClick={onOpenProject}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-white hover:text-indigo-700">
            Ouvrir la fiche <ExternalLink size={12} />
          </button>
        </header>
        {data.carnet.summary && (
          <p className="flex gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm leading-relaxed text-slate-600 ring-1 ring-slate-200/70">
            <Sparkles size={14} className="mt-1 shrink-0 text-indigo-400" />
            <span>{data.carnet.summary}</span>
          </p>
        )}
      </div>
      <div className="min-w-0 lg:sticky lg:top-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pb-1">{panel}</div>
      <div className="min-w-0 space-y-5 lg:col-start-1 lg:row-start-2">
        <Since data={data} />
        <Remaining data={data} />
      </div>
    </div>
  );
};

export default ProjectReview;
